/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

// @ts-nocheck

import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  addErrorMessage,
  addMessage,
  ToastStatus
} from '@ibm-watson/shared/modules/ia/store/messages/actions';
import { ApiStatus } from '../../../ts';
import {
  GetElasticIndicesResponse,
  GetElserSupportResponse,
  GetIndexMappingResponse,
  ListDocsResponse,
  IngestDocResponse,
  CreateElserResponse
} from '../../../ts/elastic';
import { ELASTIC_SEARCH_AUTH_TYPES } from '../../../ts/skill';
import type { CreateAsyncThunk } from '../../../ts/state';
import * as elasticApi from '../api';
import {
  selectDraftElasticUrl,
  selectDraftElasticPort,
  selectElasticAuthType,
  selectDraftElasticUser,
  selectDraftElasticPass,
  selectDraftElasticApiKey,
  selectDraftElasticIndex
} from './selectors';

const refineErrorMsg = error => {
  if (error.status === 401) {
    error.title = '401 status: Invalid credentials';
    error.message = error?.error?.reason;
  } else if (error.status === 404) {
    error.title = '404 status';
    error.message = error?.error?.reason;
  }

  return error;
};

export const createElser = createAsyncThunk<
  CreateElserResponse,
  { skillId: string; assistantId: string; index: string },
  CreateAsyncThunk
>(
  'elastic/createElser',
  async (params, { dispatch, getState }) => {
    const { skillId, assistantId, index } = params;
    const url = selectDraftElasticUrl(getState());
    const port = selectDraftElasticPort(getState());
    const authType = selectElasticAuthType(getState());

    let auth;
    if (authType === ELASTIC_SEARCH_AUTH_TYPES.BASIC) {
      auth = {
        username: selectDraftElasticUser(getState()),
        password: selectDraftElasticPass(getState())
      };
    } else if (authType === ELASTIC_SEARCH_AUTH_TYPES.API) {
      auth = { apiKey: selectDraftElasticApiKey(getState()) };
    }

    try {
      let authToken;
      if (auth && auth.username && auth.password) {
        const encodedUserPass = Buffer.from(
          auth.username + ':' + auth.password
        ).toString('base64');
        authToken = 'Basic ' + encodedUserPass;
      } else if (auth && auth.apiKey) {
        authToken = 'ApiKey ' + auth.apiKey;
      }

      const response = await elasticApi.createElser(
        skillId,
        assistantId,
        url,
        authToken,
        index,
        port
      );
      return response;
    } catch (error) {
      const updatedError = refineErrorMsg(error);
      dispatch(addErrorMessage(updatedError));
      throw error;
    }
  },
  {
    // this condition parameter passed to createAsyncThunk causes the call to cancel
    //  (with no payload processing) if we already have an earlier call in progress
    condition: (_, { getState }) => {
      const { createElserStatus } = getState()?.elastic;
      if (createElserStatus === ApiStatus.LOADING) {
        return false;
      }
    }
  }
);

export const getElserSupport = createAsyncThunk<
  GetElserSupportResponse,
  { skillId: string; assistantId: string },
  CreateAsyncThunk
>(
  'elastic/getElserSupport',
  async (params, { dispatch, getState }) => {
    const { skillId, assistantId } = params;
    const url = selectDraftElasticUrl(getState());
    const port = selectDraftElasticPort(getState());
    const authType = selectElasticAuthType(getState());

    let auth;
    if (authType === ELASTIC_SEARCH_AUTH_TYPES.BASIC) {
      auth = {
        username: selectDraftElasticUser(getState()),
        password: selectDraftElasticPass(getState())
      };
    } else if (authType === ELASTIC_SEARCH_AUTH_TYPES.API) {
      auth = { apiKey: selectDraftElasticApiKey(getState()) };
    }

    try {
      let authToken;
      if (auth && auth.username && auth.password) {
        const encodedUserPass = Buffer.from(
          auth.username + ':' + auth.password
        ).toString('base64');
        authToken = 'Basic ' + encodedUserPass;
      } else if (auth && auth.apiKey) {
        authToken = 'ApiKey ' + auth.apiKey;
      }

      const response = await elasticApi.getElserSupport(
        skillId,
        assistantId,
        url,
        authToken,
        port
      );

      return response;
    } catch (error) {
      const updatedError = refineErrorMsg(error?.response?.data);
      dispatch(
        addMessage({
          title: updatedError.title,
          message: updatedError.message,
          status: ToastStatus.ERROR
        })
      );
      throw error;
    }
  },
  {
    // this condition parameter passed to createAsyncThunk causes the call to cancel
    //  (with no payload processing) if we already have an earlier call in progress
    condition: (_, { getState }) => {
      const { isElserSupportedStatus } = getState()?.elastic;
      if (isElserSupportedStatus === ApiStatus.LOADING) {
        return false;
      }
    }
  }
);

export const getIndexMapping = createAsyncThunk<
  GetIndexMappingResponse,
  { skillId: string; assistantId: string },
  CreateAsyncThunk
>(
  'elastic/getIndexMapping',
  async (params, { dispatch, getState }) => {
    const { skillId, assistantId } = params;
    const url = selectDraftElasticUrl(getState());
    const port = selectDraftElasticPort(getState());
    const authType = selectElasticAuthType(getState());
    const index = selectDraftElasticIndex(getState());

    let auth;
    if (authType === ELASTIC_SEARCH_AUTH_TYPES.BASIC) {
      auth = {
        username: selectDraftElasticUser(getState()),
        password: selectDraftElasticPass(getState())
      };
    } else if (authType === ELASTIC_SEARCH_AUTH_TYPES.API) {
      auth = { apiKey: selectDraftElasticApiKey(getState()) };
    }

    try {
      let authToken;
      if (auth && auth.username && auth.password) {
        const encodedUserPass = Buffer.from(
          auth.username + ':' + auth.password
        ).toString('base64');
        authToken = 'Basic ' + encodedUserPass;
      } else if (auth && auth.apiKey) {
        authToken = 'ApiKey ' + auth.apiKey;
      }

      const response = await elasticApi.getIndexMapping(
        skillId,
        assistantId,
        url,
        authToken,
        index,
        port
      );
      return response;
    } catch (error) {
      const updatedError = refineErrorMsg(error);
      dispatch(addErrorMessage(updatedError));
      throw error;
    }
  },
  {
    // this condition parameter passed to createAsyncThunk causes the call to cancel
    //  (with no payload processing) if we already have an earlier call in progress
    condition: (_, { getState }) => {
      const { contentMappingStatus } = getState()?.elastic;
      if (contentMappingStatus === ApiStatus.LOADING) {
        return false;
      }
    }
  }
);

export const ingestDocs = createAsyncThunk<
  IngestDocResponse,
  { skillId: string; assistantId: string; files: File[] },
  CreateAsyncThunk
>(
  'elastic/ingestDocs',
  async (params, { dispatch, getState }) => {
    const { skillId, assistantId, files } = params;
    const url = selectDraftElasticUrl(getState());
    const port = selectDraftElasticPort(getState());
    const authType = selectElasticAuthType(getState());
    const index = selectDraftElasticIndex(getState());

    let auth;
    if (authType === ELASTIC_SEARCH_AUTH_TYPES.BASIC) {
      auth = {
        username: selectDraftElasticUser(getState()),
        password: selectDraftElasticPass(getState())
      };
    } else if (authType === ELASTIC_SEARCH_AUTH_TYPES.API) {
      auth = { apiKey: selectDraftElasticApiKey(getState()) };
    }

    try {
      let authToken;
      if (auth && auth.username && auth.password) {
        const encodedUserPass = Buffer.from(
          auth.username + ':' + auth.password
        ).toString('base64');
        authToken = 'Basic ' + encodedUserPass;
      } else if (auth && auth.apiKey) {
        authToken = 'ApiKey ' + auth.apiKey;
      }

      const response = await elasticApi.ingestDocs(
        skillId,
        assistantId,
        url,
        authToken,
        index,
        files,
        port
      );

      //Throw error
      if (response.hasOwnProperty('errors')) {
        if (response.errors) {
          let errorMessage = '';

          response?.items.map((item, index) => {
            if (item?.create?.error) {
              errorMessage += `${files[index].name}: ${item?.create?.error?.reason}. `;
            }
          });

          await dispatch(
            addMessage({
              message: errorMessage,
              status: ToastStatus.ERROR
            })
          );
        } else {
          await dispatch(
            addMessage({
              title: 'Documents sent',
              message: `Processing has begun. Timestamp: ${new Date().toLocaleString()}`,
              status: ToastStatus.INFO
            })
          );
        }
      }

      return response;
    } catch (error) {
      const updatedError = refineErrorMsg(error);
      dispatch(addErrorMessage(updatedError));
      throw error;
    }
  },
  {
    // this condition parameter passed to createAsyncThunk causes the call to cancel
    //  (with no payload processing) if we already have an earlier call in progress
    condition: (_, { getState }) => {
      const { filesStatus } = getState()?.elastic;
      if (filesStatus === ApiStatus.LOADING) {
        return false;
      }
    }
  }
);
export const listDocs = createAsyncThunk<
  ListDocsResponse,
  { skillId: string; assistantId: string; noLoad?: boolean },
  CreateAsyncThunk
>(
  'elastic/listDocs',
  async ({ skillId, assistantId }, { dispatch, getState }) => {
    const url = selectDraftElasticUrl(getState());
    const port = selectDraftElasticPort(getState());
    const authType = selectElasticAuthType(getState());
    const index = selectDraftElasticIndex(getState());

    let auth;
    if (authType === ELASTIC_SEARCH_AUTH_TYPES.BASIC) {
      auth = {
        username: selectDraftElasticUser(getState()),
        password: selectDraftElasticPass(getState())
      };
    } else if (authType === ELASTIC_SEARCH_AUTH_TYPES.API) {
      auth = { apiKey: selectDraftElasticApiKey(getState()) };
    }

    try {
      let authToken;
      if (auth && auth.username && auth.password) {
        const encodedUserPass = Buffer.from(
          auth.username + ':' + auth.password
        ).toString('base64');
        authToken = 'Basic ' + encodedUserPass;
      } else if (auth && auth.apiKey) {
        authToken = 'ApiKey ' + auth.apiKey;
      }

      const response = await elasticApi.listDocs(
        skillId,
        assistantId,
        url,
        authToken,
        index,
        port
      );
      return response?.hits?.hits;
    } catch (error) {
      const updatedError = refineErrorMsg(error?.response?.data);
      dispatch(
        addMessage({
          title: updatedError.title,
          message: updatedError.message,
          status: ToastStatus.ERROR
        })
      );
      throw error;
    }
  },
  {
    // this condition parameter passed to createAsyncThunk causes the call to cancel
    //  (with no payload processing) if we already have an earlier call in progress
    condition: (_, { getState }) => {
      const { uploadFilesStatus } = getState()?.elastic;
      if (uploadFilesStatus === ApiStatus.LOADING) {
        return false;
      }
    }
  }
);

export const deleteDocs = createAsyncThunk<
  ListDocsResponse,
  { skillId: string; assistantId: string; docIds: string[] },
  CreateAsyncThunk
>(
  'elastic/deleteDocs',
  async (params, { dispatch, getState }) => {
    const { skillId, assistantId, docIds } = params;
    const url = selectDraftElasticUrl(getState());
    const port = selectDraftElasticPort(getState());
    const authType = selectElasticAuthType(getState());
    const index = selectDraftElasticIndex(getState());

    let auth;
    if (authType === ELASTIC_SEARCH_AUTH_TYPES.BASIC) {
      auth = {
        username: selectDraftElasticUser(getState()),
        password: selectDraftElasticPass(getState())
      };
    } else if (authType === ELASTIC_SEARCH_AUTH_TYPES.API) {
      auth = { apiKey: selectDraftElasticApiKey(getState()) };
    }

    try {
      let authToken;
      if (auth && auth.username && auth.password) {
        const encodedUserPass = Buffer.from(
          auth.username + ':' + auth.password
        ).toString('base64');
        authToken = 'Basic ' + encodedUserPass;
      } else if (auth && auth.apiKey) {
        authToken = 'ApiKey ' + auth.apiKey;
      }

      const response = await elasticApi.deleteDocs(
        skillId,
        assistantId,
        url,
        authToken,
        index,
        docIds,
        port
      );
      return response?.hits?.hits;
    } catch (error) {
      const updatedError = refineErrorMsg(error);
      dispatch(addErrorMessage(updatedError));
      throw error;
    }
  },
  {
    // this condition parameter passed to createAsyncThunk causes the call to cancel
    //  (with no payload processing) if we already have an earlier call in progress
    condition: (_, { getState }) => {
      const { uploadFilesStatus } = getState()?.elastic;
      if (uploadFilesStatus === ApiStatus.LOADING) {
        return false;
      }
    }
  }
);

export const listIndices = createAsyncThunk<
  GetElasticIndicesResponse,
  { skillId: string; assistantId: string },
  CreateAsyncThunk
>(
  'elastic/listIndices',
  async (params, { dispatch, getState }) => {
    const { skillId, assistantId } = params;
    const url = selectDraftElasticUrl(getState());
    const port = selectDraftElasticPort(getState());
    const authType = selectElasticAuthType(getState());

    try {
      let auth;
      if (authType === ELASTIC_SEARCH_AUTH_TYPES.BASIC) {
        auth = {
          username: selectDraftElasticUser(getState()),
          password: selectDraftElasticPass(getState())
        };
      } else if (authType === ELASTIC_SEARCH_AUTH_TYPES.API) {
        auth = { apiKey: selectDraftElasticApiKey(getState()) };
      }

      let authToken;
      if (auth && auth.username && auth.password) {
        const encodedUserPass = Buffer.from(
          auth.username + ':' + auth.password
        ).toString('base64');
        authToken = 'Basic ' + encodedUserPass;
      } else if (auth && auth.apiKey) {
        authToken = 'ApiKey ' + auth.apiKey;
      }

      const response = await elasticApi.getElasticIndices(
        skillId,
        assistantId,
        url,
        authToken,
        port
      );
      const indices = response instanceof Array ? response : [];

      return { indices };
    } catch (error) {
      const updatedError = refineErrorMsg(error?.response?.data);
      dispatch(
        addMessage({
          title: updatedError.title,
          message: updatedError.message,
          status: ToastStatus.ERROR
        })
      );
      throw error;
    }
  },
  {
    // this condition parameter passed to createAsyncThunk causes the call to cancel
    //  (with no payload processing) if we already have an earlier call in progress
    condition: (_, { getState }) => {
      const { indicesStatus } = getState()?.elastic;
      if (indicesStatus === ApiStatus.LOADING) {
        return false;
      }
    }
  }
);
